
import Page from '@/Page.vue';
import { Component, Ref } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

import {
    getOeeSummary,
    getOeeDaily,
    OeeDailyPoint,
    getOeeByWorkplace,
    OeeWorkplaceSummary,
    getWorldClassTarget,
    getGlobalTargetOee,
    getOeeTrendByWorkplace,
    OeeTrendByWorkplacePoint,
} from '@/libs/tpm-analytics/tpm-analytics-data-service';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { WorkplaceModel } from '@/libs/Api';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import {
    getFilterById,
    unpackSettings,
    getSelectedFilterLocal,
    setSelectedFilterLocal,
} from '@/libs/tpm-analytics/core/saved-filters';
import { getDateRange, getPreviousDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { resetMissingCycleTimes } from '@/libs/tpm-analytics/core/missing-cycle-times';
import {
    saveChartZoom,
    loadChartZoom,
    saveCategoryZoom,
    loadCategoryZoom,
    getOptimalWindowSize,
    clearChartZoom,
} from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsOee extends Page {
    availability: number | null = null;
    performance: number | null = null;
    quality: number | null = null;
    oee: number | null = null;
    daily: OeeDailyPoint[] = [];
    @Ref('chartScroll') chartScroll!: HTMLElement;
    viewStart: number | null = null;
    viewEnd: number | null = null;
    // Pro kategorickou osu: indexy viditelných kategorií
    categoryStartIndex: number | null = null;
    categoryEndIndex: number | null = null;
    workplaces: OeeWorkplaceSummary[] = [];
    workplaceTrendData: OeeTrendByWorkplacePoint[] = [];
    worldClassTarget: number | null = null; // 0..1
    oeeTarget: number | null = null; // 0..1
    targetsMap: Map<string | null, { worldClass?: number | null; oeeTarget?: number | null }> = new Map();
    // viewport for workplaces chart (category axis)
    wpStartIndex: number | null = null;
    wpEndIndex: number | null = null;
    previousGranularity: 'day' | 'week' | 'month' | null = null;
    workplaceChartMode: 'trend' | 'average' = 'trend';
    selectedOeeMetric: 'oee' | 'availability' | 'performance' | 'quality' = 'oee';

    // Filters
    periodPreset:
        | 'previousDay'
        | 'last7d'
        | 'last14d'
        | 'last30d'
        | 'last3m'
        | 'thisWeek'
        | 'lastWeek'
        | 'thisMonth'
        | 'lastMonth'
        | 'thisQuarter'
        | 'lastQuarter'
        | 'thisYear'
        | 'lastYear'
        | 'custom' = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];
    loading = false;
    oeeGranularity: 'day' | 'week' | 'month' = 'day';
    initialViewportApplied = false;
    applyingInitial = false;
    filtersDebounceTimer: any = null;

    // Previous period for comparison
    prevAvailability: number | null = null;
    prevPerformance: number | null = null;
    prevQuality: number | null = null;
    prevOee: number | null = null;
    prevWorkplaces: OeeWorkplaceSummary[] = [];

    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    get windowDays(): number {
        return getOptimalWindowSize(this.oeeGranularity);
    }

    get wpWindow(): number {
        return getOptimalWindowSize(this.oeeGranularity);
    }

    private getTimeUnit(gran: 'day' | 'week' | 'month'): 'day' | 'isoWeek' | 'month' {
        if (gran === 'week') return 'isoWeek';
        if (gran === 'month') return 'month';
        return 'day';
    }

    mounted() {
        // V mounted() voláme load() pouze pokud už není applyingInitial
        // (pokud je, created() ho zavolá až se načte filtr)
        this.logZoom('mounted');
        if (!this.applyingInitial) {
            this.load();
        }
        // Po dalším ticku zkusíme do-logovat skutečné min/max z ApexCharts
        this.$nextTick(() => {
            try {
                const comp: any = (this.$refs as any).oeeChart;
                const chart = comp && comp.chart && comp.chart.w && comp.chart.w.globals;
                if (chart && chart.minX != null && chart.maxX != null) {
                    this.logZoom('apex:initialExtents', { minX: chart.minX, maxX: chart.maxX });
                }
            } catch (e) {
                // Ignore errors when accessing chart internals
            }
        });
        // Watch pro změnu granularity
        this.$watch('oeeGranularity', (newVal: 'day' | 'week' | 'month', oldVal: 'day' | 'week' | 'month') => {
            this.logZoom('granularityChanged', { oldVal, newVal });
            if (oldVal && newVal !== oldVal) {
                // Při změně granularity resetujeme zoom
                this.previousGranularity = null;
                this.resetViewport(true);
                this.wpResetViewport();
            }
        });
        
        // Watch pro změnu workplaceChartMode
        this.$watch('workplaceChartMode', (newVal: 'trend' | 'average', oldVal: 'trend' | 'average') => {
            if (oldVal && newVal !== oldVal && !this.applyingInitial) {
                // Aktualizuj state v parent pro synchronizaci mezi taby
                const currentState = this.getCurrentFilterState();
                if ((this.$root as any).$tpmOnStateChanged) {
                    (this.$root as any).$tpmOnStateChanged(currentState);
                }
                // Při změně režimu resetujeme zoom a načteme data
                this.wpResetViewport();
                this.load();
            }
        });
        this.$watch('selectedOeeMetric', (newVal: string, oldVal: string) => {
            if (oldVal && newVal !== oldVal && !this.applyingInitial) {
                // Aktualizuj state v parent pro synchronizaci mezi taby
                const currentState = this.getCurrentFilterState();
                if ((this.$root as any).$tpmOnStateChanged) {
                    (this.$root as any).$tpmOnStateChanged(currentState);
                }
                // Metrika se změnila - pouze aktualizuj graf (data už máme načtená)
            }
        });
    }

    get periodItems() {
        return [
            { text: this.$t('tpmFilters.previousDay') as any, value: 'previousDay' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.last7d') as any, value: 'last7d' },
            { text: this.$t('tpmFilters.last14d') as any, value: 'last14d' },
            { text: this.$t('tpmFilters.last30d') as any, value: 'last30d' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.preset7d') as any, value: '7d' },
            { text: this.$t('tpmFilters.preset1m') as any, value: '1m' },
            { text: this.$t('tpmFilters.preset3m') as any, value: '3m' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.thisWeek') as any, value: 'thisWeek' },
            { text: this.$t('tpmFilters.lastWeek') as any, value: 'lastWeek' },
            { text: this.$t('tpmFilters.thisMonth') as any, value: 'thisMonth' },
            { text: this.$t('tpmFilters.lastMonth') as any, value: 'lastMonth' },
            { text: this.$t('tpmFilters.thisQuarter') as any, value: 'thisQuarter' },
            { text: this.$t('tpmFilters.lastQuarter') as any, value: 'lastQuarter' },
            { text: this.$t('tpmFilters.thisYear') as any, value: 'thisYear' },
            { text: this.$t('tpmFilters.lastYear') as any, value: 'lastYear' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.presetCustom') as any, value: 'custom' },
        ];
    }

    getRange() {
        return getDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.oeeGranularity,
        });
    }

    async load() {
        if (!this.tenantId) return;
        if (this.loading) {
            this.logZoom('load:skipAlreadyLoading');
            return;
        }
        // Před novým načtením vždy odemkneme počáteční aplikaci zoomu
        this.initialViewportApplied = false;
        const range = this.getRange();
        this.logZoom('load:start', { ...range, granularity: this.oeeGranularity });
        this.loading = true;
        // Reset missing cycle times na začátku celého načítání (ne v každé service funkci)
        resetMissingCycleTimes();
        const { from, to } = range;
        const { from: prevFrom, to: prevTo } = getPreviousDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
        });
        this.loading = true;
        const res = await getOeeSummary({
            tenantId: this.tenantId,
            from,
            to,
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        });
        this.availability = res.availability;
        this.performance = res.performance;
        this.quality = res.quality;
        this.oee = res.oee;
        this.daily = await getOeeDaily({
            tenantId: this.tenantId,
            from,
            to,
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.oeeGranularity as any,
        });
        this.logZoom('load:dailyLoaded', { count: this.daily.length });

        // Detailní log dat z backendu
        console.log('[OEE][load] Daily data from backend:');
        this.daily.forEach((d, idx) => {
            const m = moment(d.day);
            console.log('[OEE][load] Data point', idx, ':', {
                day: d.day,
                formatted: m.format('YYYY-MM-DD HH:mm:ss'),
                week: m.isoWeek(),
                year: m.year(),
                oee: d.oee,
                dayOfWeek: m.format('dddd'),
            });
        });
        // Load workplace data based on chart mode
        console.log('[OEE][load] Workplace chart mode:', this.workplaceChartMode);
        if (this.workplaceChartMode === 'trend') {
            // Load trend data for workplace chart
            console.log('[OEE][load] Loading trend data...');
            this.workplaceTrendData = await getOeeTrendByWorkplace({
                tenantId: this.tenantId,
                from,
                to,
                locationIds: this.selectedLocationIds,
                workplaceIds: this.selectedWorkplaceIds,
                projectIds: this.selectedProjectIds,
                shiftTypeIds: this.selectedShiftTypeIds,
                shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
                granularity: this.oeeGranularity as any,
            });
            console.log('[OEE][load] Trend data loaded:', this.workplaceTrendData.length, 'periods');
            if (this.workplaceTrendData.length > 0) {
                console.log('[OEE][load] First period sample:', {
                    key: this.workplaceTrendData[0].periodKey,
                    workplaces: this.workplaceTrendData[0].workplaceData.size,
                });
            }
            // Also load average data for the detail table
            this.workplaces = await getOeeByWorkplace({
                tenantId: this.tenantId,
                from,
                to,
                locationIds: this.selectedLocationIds,
                workplaceIds: this.selectedWorkplaceIds,
                projectIds: this.selectedProjectIds,
                shiftTypeIds: this.selectedShiftTypeIds,
                shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            });
        } else {
            // Load average data only
            console.log('[OEE][load] Loading average data only...');
            this.workplaces = await getOeeByWorkplace({
                tenantId: this.tenantId,
                from,
                to,
                locationIds: this.selectedLocationIds,
                workplaceIds: this.selectedWorkplaceIds,
                projectIds: this.selectedProjectIds,
                shiftTypeIds: this.selectedShiftTypeIds,
                shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            });
        }
        console.log('[OEE][load] Workplaces loaded:', this.workplaces.length);
        // Previous summary
        const prev = await getOeeSummary({
            tenantId: this.tenantId,
            from: prevFrom,
            to: prevTo,
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        });
        this.prevAvailability = prev.availability;
        this.prevPerformance = prev.performance;
        this.prevQuality = prev.quality;
        this.prevOee = prev.oee;

        this.worldClassTarget = await getWorldClassTarget(from, to);
        this.oeeTarget = await getGlobalTargetOee(from, to);
        // Per-workplace targets
        try {
            const { getTargetsPerWorkplace } = await import('@/libs/tpm-analytics/tpm-analytics-data-service');
            const map = await getTargetsPerWorkplace(
                from,
                to,
                this.workplaces.map((w: any) => ({ workplaceId: w.workplaceId ?? null, locationId: w.locationId ?? null }))
            );
            this.targetsMap = map;
        } catch {
            /* ignore */
        }

        // Previous workplaces
        this.prevWorkplaces = await getOeeByWorkplace({
            tenantId: this.tenantId,
            from: prevFrom,
            to: prevTo,
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        });
        if (!this.initialViewportApplied) {
            this.logZoom('load:applyInitialViewport');
            this.resetViewport(true);
        } else {
            this.logZoom('load:skipViewportResetAlreadyApplied');
        }
        this.wpResetViewport();
        this.loading = false;
        this.logZoom('load:done');
    }

    onFiltersChanged() {
        if (this.applyingInitial) return;
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            this.load();
            const fn = (this.$root as any).$tpmOnStateChanged;
            if (typeof fn === 'function') fn();
        }, 200);
    }

    // Watchers for period
    $watch!: any;
    created() {
        // Register callbacks for parent
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);

        // Pokud načítáme async filtr, nastavíme flag pro mounted()
        this.applyingInitial = true;

        // V created() pouze NASTAVÍME stav (reload = false), data se načtou v mounted()
        const initState = (this.$root as any).$tpmInitialState;
        const initSid = (this.$root as any).$tpmInitialSid;
        if (initState && Object.keys(initState || {}).length > 0) {
            this.applyFilterState(initState, false);
            this.applyingInitial = false;
            return;
        }
        if (initSid) {
            setSelectedFilterLocal(initSid);
            getFilterById(initSid).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
                this.applyingInitial = false;
                // Po načtení filtru explicitně zavoláme load
                this.load();
            }).catch((err) => {
                console.error('[OEE] Error loading filter:', err);
                this.applyingInitial = false;
                this.load();
            });
            return;
        }
        const localId = getSelectedFilterLocal();
        if (localId) {
            setSelectedFilterLocal(localId);
            getFilterById(localId).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
                this.applyingInitial = false;
                // Po načtení filtru explicitně zavoláme load
                this.load();
            }).catch((err) => {
                console.error('[OEE] Error loading filter:', err);
                this.applyingInitial = false;
                this.load();
            });
            return;
        }
        // Pokud není žádný initial state, mounted() zavolá load() s defaultními hodnotami
        this.applyingInitial = false;
    }

    destroyed() {
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState)
            delete (this.$root as any).$tpmGetCurrentState;
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) delete (this.$root as any).$tpmApplyState;
    }

    getCurrentFilterState() {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.oeeGranularity,
            workplaceChartMode: this.workplaceChartMode,
            selectedOeeMetric: this.selectedOeeMetric,
        } as any;
    }
    applyFilterState(state: any, reload = true) {
        // reload = true: uživatel změnil filtry → načti data
        // reload = false: inicializace v created() → pouze nastav stav, data se načtou v mounted()
        const periodChanged = state?.periodPreset && state.periodPreset !== this.periodPreset;
        const customDatesChanged =
            (state?.customFrom && state.customFrom !== this.customFrom) ||
            (state?.customTo && state.customTo !== this.customTo);

        this.applyingInitial = true;
        try {
            if (state?.periodPreset) this.periodPreset = state.periodPreset;
            if (state?.customFrom) this.customFrom = state.customFrom;
            if (state?.customTo) this.customTo = state.customTo;
            this.selectedLocationIds = Array.isArray(state?.selectedLocationIds) ? state.selectedLocationIds : [];
            this.selectedWorkplaceIds = Array.isArray(state?.selectedWorkplaceIds) ? state.selectedWorkplaceIds : [];
            this.selectedProjectIds = Array.isArray(state?.selectedProjectIds) ? state.selectedProjectIds : [];
            this.selectedShiftTypeIds = Array.isArray(state?.selectedShiftTypeIds) ? state.selectedShiftTypeIds : [];
            this.selectedShiftTeamTypeIds = Array.isArray(state?.selectedShiftTeamTypeIds)
                ? state.selectedShiftTeamTypeIds
                : [];
            if (state?.granularity) {
                this.oeeGranularity = state.granularity;
            } else if (state?.oeeGranularity) {
                // Zpětná kompatibilita se starými uloženými filtry
                this.oeeGranularity = state.oeeGranularity;
            }
            if (state?.workplaceChartMode) {
                this.workplaceChartMode = state.workplaceChartMode;
            }
            if (state?.selectedOeeMetric) {
                this.selectedOeeMetric = state.selectedOeeMetric;
            }
        } finally {
            this.applyingInitial = false;
        }

        // Pokud se změnilo období, vymazat uložený zoom aby se použil nový default (maximum dat)
        if (periodChanged || customDatesChanged) {
            clearChartZoom('oee-daily');
        }

        if (reload) {
            this.onFiltersChanged();
        }
    }

    get currentPeriodText() {
        const { from, to } = this.getRange();
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(from)} – ${fmt(to)}`;
    }
    get previousPeriodText() {
        const { from: prevFrom, to: prevTo } = getPreviousDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.oeeGranularity,
        });
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(prevFrom)} – ${fmt(prevTo)}`;
    }

    // ======= Comparisons =======
    private pctChange(curr: number | null, prev: number | null): number | null {
        if (curr == null || prev == null) return null;
        const p = Number(prev);
        if (!isFinite(p) || Math.abs(p) < 1e-9) return null;
        const c = Number(curr);
        return ((c - p) / p) * 100;
    }

    get deltaOeePct(): number | null {
        return this.pctChange(this.oee, this.prevOee);
    }
    get deltaAvailabilityPct(): number | null {
        return this.pctChange(this.availability, this.prevAvailability);
    }
    get deltaPerformancePct(): number | null {
        return this.pctChange(this.performance, this.prevPerformance);
    }
    get deltaQualityPct(): number | null {
        return this.pctChange(this.quality, this.prevQuality);
    }

    rowDelta(
        workplaceId: string | null,
        field: 'oee' | 'availability' | 'performance' | 'quality' | 'downtimeMinutes' | 'products' | 'scraps'
    ): number | null {
        const curr = this.workplaces.find((w) => w.workplaceId === workplaceId);
        const prev = this.prevWorkplaces.find((w) => w.workplaceId === workplaceId);
        if (!curr || !prev) return null;
        return this.pctChange((curr as any)[field] ?? null, (prev as any)[field] ?? null);
    }

    getPrevRowValue(
        workplaceId: string | null,
        field: 'oee' | 'availability' | 'performance' | 'quality' | 'downtimeMinutes' | 'products' | 'scraps'
    ): number | null {
        const prev = this.prevWorkplaces.find((w) => w.workplaceId === workplaceId);
        if (!prev) return null;
        return (prev as any)[field] ?? null;
    }

    formatDelta(v: number): string {
        const s = v >= 0 ? '+' : '';
        return `${s}${v.toFixed(1)}%`;
    }

    formatPct(v: number | null): string {
        if (v == null || isNaN(v as any)) return '-';
        const clamped = Math.max(0, Math.min(1, Number(v)));
        return (clamped * 100).toFixed(1) + '%';
    }

    formatNum(v: number | null | undefined): string {
        if (v == null || isNaN(v as any)) return '-';
        return new Intl.NumberFormat('cs-CZ').format(Number(v));
    }

    formatDowntimeHours(v: number | null | undefined): string {
        if (v == null || isNaN(v as any)) return '-';
        const hours = Number(v) / 60;
        return new Intl.NumberFormat('cs-CZ', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(hours);
    }

    // Agregovaná data pro graf
    get aggregatedDataForChart(): OeeDailyPoint[] {
        if (this.oeeGranularity === 'day') {
            // Pro denní zobrazení neagregujeme
            return this.daily;
        }

        // Získáme offset výrobního dne
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        // Seskupíme data podle období (týden nebo měsíc)
        const grouped = new Map<number, OeeDailyPoint[]>();

        this.daily.forEach((d) => {
            let periodKey: number;
            if (this.oeeGranularity === 'week') {
                // Začátek týdne + offset výrobního dne
                periodKey = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else {
                // Začátek měsíce + offset výrobního dne
                periodKey = moment(d.day).startOf('month').valueOf() + dayStartMs;
            }

            if (!grouped.has(periodKey)) {
                grouped.set(periodKey, []);
            }
            grouped.get(periodKey)!.push(d);
        });

        // Pro každé období vezmeme průměr hodnot
        const aggregated: OeeDailyPoint[] = [];
        grouped.forEach((points, periodKey) => {
            // Průměr hodnot
            const avg = (values: (number | null)[]): number | null => {
                const valid = values.filter((v) => v != null && isFinite(v as any)) as number[];
                if (valid.length === 0) return null;
                return valid.reduce((sum, v) => sum + v, 0) / valid.length;
            };

            aggregated.push({
                day: moment(periodKey).toISOString(),
                oee: avg(points.map((p) => p.oee)),
                availability: avg(points.map((p) => p.availability)),
                performance: avg(points.map((p) => p.performance)),
                quality: avg(points.map((p) => p.quality)),
            });
        });

        // Seřadit podle data
        return aggregated.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }

    get chartOptions() {
        const self = this;
        
        // Vygenerujeme kategorie (labely) pro osu X
        const categories = this.aggregatedDataForChart.map((d) => {
            const dayStartMinutes = getDayStartMinutes();
            const dayStartMs = dayStartMinutes * 60 * 1000;
            let timestamp: number;
            
            if (this.oeeGranularity === 'week') {
                timestamp = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.oeeGranularity === 'month') {
                timestamp = moment(d.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(d.day).startOf('day').valueOf() + dayStartMs;
            }
            
            return self.formatAxisLabel(timestamp, self.oeeGranularity);
        });

        console.log('[OEE][chartOptions] Categories:', {
            totalCategories: categories.length,
            categoryStartIndex: this.categoryStartIndex,
            categoryEndIndex: this.categoryEndIndex,
            firstCategory: categories[0],
            lastCategory: categories[categories.length - 1],
            visibleRange: this.categoryStartIndex != null && this.categoryEndIndex != null 
                ? `${this.categoryStartIndex} - ${this.categoryEndIndex} (showing ${this.categoryEndIndex - this.categoryStartIndex + 1} of ${categories.length})`
                : 'ALL',
        });

        return {
            chart: {
                id: 'oee-daily',
                type: 'line',
                stacked: false,
                zoom: { enabled: false },
                toolbar: { show: false },
                animations: { enabled: false },
            },
            xaxis: {
                type: 'category',
                categories: categories,
                // Pro kategorickou osu je max EXCLUSIVE, takže musíme přičíst 1
                // Pro min používáme -0.8 když začínáme od 0, aby se sloupec maximálně přiblížil k levému okraji
                min: this.categoryStartIndex === 0 ? -0.8 : (this.categoryStartIndex != null ? this.categoryStartIndex - 0.3 : undefined),
                max: this.categoryEndIndex != null ? this.categoryEndIndex + 1.3 : undefined,
                labels: {
                    rotate: self.oeeGranularity === 'week' ? -45 : 0,
                    style: {
                        cssClass: 'apexcharts-xaxis-label-multiline',
                    },
                    formatter(value: any) {
                        // Pro týdny rozdělíme label na dva řádky pomocí |
                        if (self.oeeGranularity === 'week' && typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts; // ApexCharts podporuje pole pro víceřádkové labely
                        }
                        return value;
                    },
                },
                axisBorder: {
                    show: true,
                },
                axisTicks: {
                    show: true,
                },
            },
            yaxis: {
                min: 0,
                max: 100,
                labels: {
                    formatter: (v: any) => {
                        const num = v == null ? NaN : Number(v);
                        if (!isFinite(num)) return '';
                        return num.toFixed(1) + '%';
                    },
                },
            },
            stroke: { width: [3, 0, 0, 0], curve: 'smooth' },
            markers: { size: 4, hover: { size: 6 } },
            dataLabels: { enabled: false },
            colors: ['#2196F3', '#4CAF50', '#FF9800', '#F44336'], // OEE-modrá, A-zelená, E-oranžová, Q-červená
            plotOptions: {
                bar: {
                    columnWidth: '95%',
                    distributed: false,
                    rangeBarGroupRows: false,
                    horizontal: false,
                    endingShape: 'flat',
                    borderRadius: 4,
                    dataLabels: {
                        position: 'top',
                    },
                },
            },
            states: {
                hover: {
                    filter: {
                        type: 'lighten',
                        value: 0.15,
                    },
                },
                active: {
                    allowMultipleDataPointsSelection: true,
                    filter: {
                        type: 'darken',
                        value: 0.35,
                    },
                },
            },
            grid: {
                padding: {
                    right: 5,
                    // Když začínáme od 0 při zoom, snížíme padding vlevo
                    left: this.categoryStartIndex === 0 ? 0 : 10,
                },
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'light',
                    shadeIntensity: 0.06,
                    opacityFrom: 0.9,
                    opacityTo: 0.85,
                    stops: [0, 80, 100],
                },
            },
            annotations:
                this.worldClassTarget != null || this.oeeTarget != null
                    ? {
                          yaxis: [
                              ...(this.worldClassTarget != null ? [{
                                  y: (this.worldClassTarget as number) * 100,
                                  borderColor: '#0b51f5',
                                  strokeDashArray: 5,
                                  label: {
                                      text: `World Class Standard (${((this.worldClassTarget as number) * 100).toFixed(0)}%)`,
                                      position: 'left',
                                      textAnchor: 'start',
                                      offsetX: 0,
                                  },
                              }] : []),
                              ...(this.oeeTarget != null ? [{
                                  y: (this.oeeTarget as number) * 100,
                                  borderColor: '#880bf5',
                                  strokeDashArray: 5,
                                  label: {
                                      text: `Cíl OEE (${((this.oeeTarget as number) * 100).toFixed(0)}%)`,
                                      position: 'left',
                                      textAnchor: 'start',
                                      offsetX: 0,
                                  },
                              }] : []),
                          ],
                      }
                    : {},
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => {
                        if (val == null || isNaN(val)) return '-';
                        return val.toFixed(1) + '%';
                    }
                }
            },
        } as any;
    }

    get chartSeries() {
        console.log('[OEE][chartSeries] Generating series for granularity:', this.oeeGranularity);
        console.log('[OEE][chartSeries] Daily data count:', this.daily.length);
        console.log('[OEE][chartSeries] Aggregated data count:', this.aggregatedDataForChart.length);

        // Získáme offset výrobního dne
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        console.log('[OEE][chartSeries] DayStart offset:', dayStartMinutes, 'minutes =', dayStartMs, 'ms');

        // Pro kategorickou osu vracíme pouze hodnoty y (bez x)
        const toValue = (v: number | null): number | null => {
            return v == null || isNaN(v as any) ? null : Number((Math.max(0, Math.min(1, Number(v))) * 100).toFixed(3));
        };

        const oee = this.aggregatedDataForChart.map((d) => toValue(d.oee));
        const a = this.aggregatedDataForChart.map((d) => toValue(d.availability));
        const e = this.aggregatedDataForChart.map((d) => toValue(d.performance));
        const q = this.aggregatedDataForChart.map((d) => toValue(d.quality));

        console.log('[OEE][chartSeries] Final series points:', oee.length);
        this.aggregatedDataForChart.forEach((d, idx) => {
            const dayStartMinutes = getDayStartMinutes();
            const dayStartMs = dayStartMinutes * 60 * 1000;
            let timestamp: number;
            
            if (this.oeeGranularity === 'week') {
                timestamp = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.oeeGranularity === 'month') {
                timestamp = moment(d.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(d.day).startOf('day').valueOf() + dayStartMs;
            }
            
            console.log('[OEE][chartSeries] Point', idx, ':', {
                category: this.formatAxisLabel(timestamp, this.oeeGranularity),
                date: moment(timestamp).format('YYYY-MM-DD HH:mm'),
                week: moment(timestamp).isoWeek(),
                value: oee[idx],
            });
        });

        return [
            { name: this.$t('tpmAnalytics.oeeShort') as any, data: oee, type: 'line' },
            { name: this.$t('tpmAnalytics.availability') as any, data: a, type: 'column' },
            { name: this.$t('tpmAnalytics.performance') as any, data: e, type: 'column' },
            { name: this.$t('tpmAnalytics.quality') as any, data: q, type: 'column' },
        ] as any;
    }

    resetViewport(force = false) {
        this.logZoom('resetViewport:called', {
            force,
            dailyCount: this.daily?.length ?? 0,
            aggregatedCount: this.aggregatedDataForChart?.length ?? 0,
        });
        if (!force && this.initialViewportApplied) return;
        const data = this.aggregatedDataForChart;
        if (data.length === 0) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            this.logZoom('resetViewport:noData');
            return;
        }

        // Zkusíme načíst uložený zoom z localStorage
        const savedZoom = loadCategoryZoom('oee-daily', this.oeeGranularity);

        if (savedZoom && savedZoom.startIndex != null && savedZoom.endIndex != null) {
            // Použijeme uložený zoom, ale ověříme, že je v platném rozsahu
            if (savedZoom.startIndex >= 0 && savedZoom.endIndex < data.length) {
                this.categoryStartIndex = savedZoom.startIndex;
                this.categoryEndIndex = savedZoom.endIndex;
                this.logZoom('resetViewport:loadedFromStorage', { 
                    categoryStartIndex: this.categoryStartIndex, 
                    categoryEndIndex: this.categoryEndIndex,
                    totalCategories: data.length
                });
                this.initialViewportApplied = true;
                return;
            }
        }

        // Pokud není uložený zoom nebo je neplatný, zobrazíme všechna data (null = všechno)
        this.categoryStartIndex = null;
        this.categoryEndIndex = null;

        this.logZoom('resetViewport:appliedDefault', { 
            categoryStartIndex: this.categoryStartIndex, 
            categoryEndIndex: this.categoryEndIndex,
            totalCategories: data.length
        });
        this.initialViewportApplied = true;
    }

    shiftPercent(percent: number) {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) return;

        let startIdx: number;
        let endIdx: number;

        // Když jsou indexy null, znamená to zobrazit všechna data
        // Nastavíme výchozí okno (např. posledních windowDays kategorií) a pak posuneme
        if (this.categoryStartIndex == null || this.categoryEndIndex == null) {
            const windowSize = Math.min(this.windowDays, data.length);
            
            if (data.length <= windowSize) {
                // Málo dat pro zoom, nelze posunout
                this.logZoom('shiftPercent:skipNotEnoughData', { dataLength: data.length, windowSize });
                return;
            }

            // Nastavíme výchozí viewport podle směru posunu
            if (percent > 0) {
                // Posun doprava -> začneme od začátku
                startIdx = 0;
                endIdx = windowSize - 1;
            } else {
                // Posun doleva -> začneme od konce
                startIdx = data.length - windowSize;
                endIdx = data.length - 1;
            }
            
            this.logZoom('shiftPercent:initViewport', { startIdx, endIdx, windowSize });
        } else {
            startIdx = this.categoryStartIndex;
            endIdx = this.categoryEndIndex;
        }

        const visibleCount = endIdx - startIdx + 1;
        // Zajistíme minimální posun 1 (aby se graf vždy pohnul, i když je málo kategorií)
        const calculatedShift = visibleCount * percent;
        const shift = percent > 0 
            ? Math.max(1, Math.round(calculatedShift))
            : Math.min(-1, Math.round(calculatedShift));

        let newStart = startIdx + shift;
        let newEnd = endIdx + shift;

        this.logZoom('shiftPercent:before', { percent, visibleCount, calculatedShift, shift, startIdx, endIdx, newStart, newEnd });

        // Bounds check - zachovat počet viditelných kategorií
        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + visibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = newEnd - visibleCount + 1;
            // Pokud by newStart vyšlo záporné, upravíme rozsah
            if (newStart < 0) {
                newStart = 0;
                newEnd = data.length - 1;
            }
        }

        // Zkontrolujeme, jestli se vůbec něco změnilo (můžeme být na kraji)
        if (newStart === startIdx && newEnd === endIdx) {
            this.logZoom('shiftPercent:noChange', { message: 'Already at boundary' });
            return;
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
        this.saveCurrentZoom();
        this.logZoom('shiftPercent:after', { categoryStartIndex: this.categoryStartIndex, categoryEndIndex: this.categoryEndIndex });
    }

    zoomIn() {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        // Přiblížit = zobrazit méně kategorií (75% z aktuálních)
        const minVisible = 2; // minimálně 2 kategorie
        const newVisibleCount = Math.max(minVisible, Math.round(visibleCount * 0.75));
        
        // Zachovat střed viewportu
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        this.logZoom('zoomIn:before', { visibleCount, newVisibleCount, center, startIdx, endIdx, newStart, newEnd });

        // Bounds check
        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
        this.saveCurrentZoom();
        this.logZoom('zoomIn:after', { categoryStartIndex: this.categoryStartIndex, categoryEndIndex: this.categoryEndIndex });
    }

    zoomOut() {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        // Oddálit = zobrazit více kategorií (133% z aktuálních)
        const maxVisible = data.length;
        const newVisibleCount = Math.min(maxVisible, Math.round(visibleCount / 0.75));
        
        this.logZoom('zoomOut:before', { visibleCount, newVisibleCount, startIdx, endIdx });

        // Pokud má být viditelné všechno, nastavíme indexy na null
        if (newVisibleCount >= data.length) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            this.saveCurrentZoom();
            this.logZoom('zoomOut:after:showAll');
            return;
        }
        
        // Zachovat střed viewportu
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        // Bounds check
        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
        this.saveCurrentZoom();
        this.logZoom('zoomOut:after', { categoryStartIndex: this.categoryStartIndex, categoryEndIndex: this.categoryEndIndex });
    }

    saveCurrentZoom() {
        const data = this.aggregatedDataForChart;
        if (data.length > 0) {
            const startIdx = this.categoryStartIndex ?? 0;
            const endIdx = this.categoryEndIndex ?? data.length - 1;
            
            this.logZoom('saveCurrentZoom', { 
                categoryStartIndex: startIdx, 
                categoryEndIndex: endIdx 
            });
            
            // Uložíme zoom jako indexy kategorií
            saveCategoryZoom('oee-daily', {
                startIndex: this.categoryStartIndex,
                endIndex: this.categoryEndIndex,
                granularity: this.oeeGranularity,
            });
        }
    }

    logZoom(event: string, extra: any = {}) {
        try {
            console.log('[OEE][Zoom]', event, {
                granularity: this.oeeGranularity,
                categoryStartIndex: this.categoryStartIndex,
                categoryEndIndex: this.categoryEndIndex,
                totalCategories: this.aggregatedDataForChart.length,
                ...extra,
            });
        } catch {
            /* ignore */
        }
    }

    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month', forTooltip = false): string {
        const m = moment(ts);
        if (!m.isValid()) {
            console.warn('[OEE][formatAxisLabel] Invalid timestamp:', ts);
            return '';
        }

        if (gran === 'month') return m.format('MM/YYYY');

        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const year = m.year();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;

            console.log('[OEE][formatAxisLabel] Week label:', {
                timestamp: ts,
                date: m.format('YYYY-MM-DD'),
                week,
                year,
                weekLabel,
                dateRange,
                forTooltip,
            });

            // Pro tooltip používáme HTML break
            if (forTooltip) {
                return `${weekLabel}<br/>${dateRange}`;
            }
            // Pro osu X vracíme pole jako string se speciálním separátorem
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }

    formatTimeRange(day: string, gran: 'day' | 'week' | 'month'): string {
        const m = moment(day);
        if (!m.isValid()) return '';

        // Data už obsahují offset výrobního dne, takže je použijeme přímo
        if (gran === 'day') {
            return m.format('DD.MM.YYYY HH:mm:ss');
        }

        if (gran === 'week') {
            // Začátek výrobního týdne je přímo v datech (s offsetem výrobního dne)
            const start = m.clone();
            // Konec výrobního týdne = začátek + 7 dní (se STEJNÝM časem jako začátek)
            // Např. 01.09. 06:00 + 7 dní = 08.09. 06:00
            const end = m.clone().add(7, 'days');
            const week = m.isoWeek();
            return `${week}. týden (${start.format('DD.MM. HH:mm')} - ${end.format('DD.MM. HH:mm')})`;
        }

        if (gran === 'month') {
            // Začátek výrobního měsíce je přímo v datech (s offsetem výrobního dne)
            const start = m.clone();
            // Konec výrobního měsíce = začátek následujícího měsíce se stejným časem
            const end = m
                .clone()
                .add(1, 'month')
                .startOf('month')
                .hour(start.hour())
                .minute(start.minute())
                .second(start.second());
            return `${start.format('MMMM YYYY')} (${start.format('DD.MM. HH:mm')} - ${end.format('DD.MM. HH:mm')})`;
        }

        return '';
    }

    get workplaceChartOptions() {
        console.log('[OEE][workplaceChartOptions] Mode:', this.workplaceChartMode);
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: X = time periods, Y = OEE values for each workplace
            const categories = this.workplaceTrendDataVisible.map((d) => {
                // Format period label based on granularity (stejně jako v hlavním grafu)
                const m = moment(d.periodKey);
                if (this.oeeGranularity === 'day') {
                    return m.format('D.M.');
                } else if (this.oeeGranularity === 'week') {
                    const start = m.clone().startOf('isoWeek');
                    const end = m.clone().endOf('isoWeek');
                    const week = m.isoWeek();
                    const weekLabel = `${week}. týden`;
                    const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
                    // Pro osu X vracíme pole jako string se speciálním separátorem |
                    return weekLabel + '|' + dateRange;
                } else {
                    return m.format('MM/YYYY');
                }
            });
            
            console.log('[OEE][workplaceChartOptions] Trend categories:', categories.length, categories);
            console.log('[OEE][workplaceChartOptions] Sample category:', categories[0]);
            
            // Rozhodnutí o rotaci popisků - pro týdny vždy rotace, pro ostatní podle počtu kategorií
            const shouldRotate = this.oeeGranularity === 'week' || categories.length > 8;
            
            return {
                chart: {
                    id: 'oee-workplace-trend',
                    type: 'bar',
                    stacked: false,
                    zoom: { enabled: false },
                    toolbar: { show: false },
                    animations: { enabled: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '85%',
                        dataLabels: {
                            position: 'top',
                        },
                    },
                },
                xaxis: {
                    categories,
                    labels: {
                        rotate: shouldRotate ? -45 : 0,
                        rotateAlways: shouldRotate,
                        hideOverlappingLabels: true,
                        trim: true,
                        minHeight: shouldRotate ? 60 : undefined,
                        maxHeight: shouldRotate ? 80 : undefined,
                        style: {
                            cssClass: 'apexcharts-xaxis-label-multiline',
                        },
                        formatter(value: any) {
                            // Pro týdny rozdělíme label na dva řádky pomocí |
                            if (typeof value === 'string' && value.includes('|')) {
                                const parts = value.split('|');
                                return parts; // ApexCharts podporuje pole pro víceřádkové labely
                            }
                            return value;
                        },
                    },
                },
                yaxis: {
                    min: 0,
                    max: 100,
                    tickAmount: 5,
                    labels: { formatter: (v: number) => v.toFixed(0) + '%' },
                },
                dataLabels: { enabled: false },
                colors: ['#1f6feb', '#60a5fa', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316'],
                stroke: {
                    show: true,
                    width: 1,
                    colors: ['transparent'],
                },
                legend: {
                    show: true,
                    horizontalAlign: 'center',
                    position: 'top',
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: (val: number) => (val == null || isNaN(val as any) ? '-' : val.toFixed(1) + '%'),
                    },
                },
                annotations: {},
            } as any;
        } else {
            // Average mode: X = workplaces, Y = average OEE
            return {
                chart: {
                    id: 'oee-workplace',
                    zoom: { enabled: false },
                    toolbar: { show: false },
                    animations: { enabled: false },
                },
                xaxis: {
                    categories: this.workplacesVisible.map((w) => w.workplaceName),
                    labels: {
                        rotate: -45,
                        rotateAlways: true,
                        hideOverlappingLabels: true,
                        trim: true,
                        minHeight: 60,
                        maxHeight: 80,
                    },
                },
                yaxis: {
                    min: 0,
                    max: 100,
                    tickAmount: 5,
                    labels: { formatter: (v: number) => v.toFixed(0) + '%' },
                },
                dataLabels: { enabled: false },
                plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
                colors: ['#1f6feb', '#60a5fa', '#10b981', '#f59e0b'],
                stroke: {
                    width: this.targetsMap && this.targetsMap.size > 0 ? [0, 2, 2] : [0],
                    dashArray: this.targetsMap && this.targetsMap.size > 0 ? [0, 6, 6] : [0],
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.08,
                        opacityFrom: 0.9,
                        opacityTo: 0.82,
                        stops: [0, 80, 100],
                    },
                },
                legend: {
                    show: true,
                    horizontalAlign: 'center',
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: (val: number) => (val == null || isNaN(val as any) ? '-' : val.toFixed(1) + '%'),
                    },
                },
                annotations: {},
            } as any;
        }
    }

    get workplaceChartSeries() {
        const toPct = (v: number | null) => {
            if (v == null || isNaN(v as any)) return null;
            const pct = Number((v * 100).toFixed(1));
            return Math.max(0, Math.min(100, pct));
        };
        const toPctTarget = (v: number | null) => {
            if (v == null || isNaN(v as any)) return null;
            const norm = v > 1 ? v / 100 : v;
            const pct = Number((norm * 100).toFixed(1));
            return Math.max(0, Math.min(100, pct));
        };
        
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: Each workplace is a separate line series
            console.log('[OEE][workplaceChartSeries] Trend mode, data count:', this.workplaceTrendDataVisible.length);
            
            // Get workplaces from store (filtrovaná dle aktuálních filtrů)
            const allWorkplaces = (workplacesStore.useGetter(CrudGetter.Data) as WorkplaceModel[]) || [];
            console.log('[OEE][workplaceChartSeries] All workplaces from store:', allWorkplaces.length);
            
            // Filter by selected workplaceIds if provided
            let filteredWorkplaces = allWorkplaces;
            if (this.selectedWorkplaceIds && this.selectedWorkplaceIds.length > 0) {
                filteredWorkplaces = allWorkplaces.filter((wp) => this.selectedWorkplaceIds.includes(wp.id!));
            }
            console.log('[OEE][workplaceChartSeries] Filtered workplaces:', filteredWorkplaces.length);
            
            const workplaceMap = new Map<string | null, { name: string; data: Array<number | null> }>();
            
            // Initialize map with filtered workplaces
            for (const wp of filteredWorkplaces) {
                const wpId = wp.id ?? null;
                workplaceMap.set(wpId, {
                    name: wp.name || 'Unknown',
                    data: [],
                });
            }
            
            console.log('[OEE][workplaceChartSeries] Initialized workplaces:', workplaceMap.size);
            
            // SECOND: Fill data for each period - iterate PERIODS first, then workplaces
            let debugCount = 0;
            for (let i = 0; i < this.workplaceTrendDataVisible.length; i++) {
                const period = this.workplaceTrendDataVisible[i];
                
                // Debug first period structure
                if (i === 0) {
                    console.log('[OEE][workplaceChartSeries] First period structure:', {
                        periodKey: period.periodKey,
                        workplaceDataType: typeof period.workplaceData,
                        isMap: period.workplaceData instanceof Map,
                        workplaceDataSize: period.workplaceData instanceof Map ? period.workplaceData.size : 'not a Map',
                        workplaceDataKeys: period.workplaceData instanceof Map ? Array.from(period.workplaceData.keys()) : 'not a Map',
                    });
                }
                
                // For this period, fill data for ALL workplaces (null if not present)
                for (const [wpId, seriesData] of workplaceMap.entries()) {
                    const wpData = period.workplaceData.get(wpId);
                    // Get value based on selected metric
                    let metricValue: number | null = null;
                    if (wpData) {
                        if (this.selectedOeeMetric === 'oee') {
                            metricValue = wpData.oee;
                        } else if (this.selectedOeeMetric === 'availability') {
                            metricValue = wpData.availability;
                        } else if (this.selectedOeeMetric === 'performance') {
                            metricValue = wpData.performance;
                        } else if (this.selectedOeeMetric === 'quality') {
                            metricValue = wpData.quality;
                        }
                    }
                    const pctValue = toPct(metricValue);
                    seriesData.data.push(pctValue);
                    
                    // Debug first few values
                    if (debugCount < 5) {
                        console.log('[OEE][workplaceChartSeries] DEBUG:', {
                            workplace: seriesData.name,
                            workplaceId: wpId,
                            period: period.periodKey,
                            wpDataExists: !!wpData,
                            selectedMetric: this.selectedOeeMetric,
                            metricValue,
                            pctValue,
                        });
                        debugCount++;
                    }
                }
            }
            
            // Convert to series array
            const series = Array.from(workplaceMap.values()).map((s) => ({
                name: s.name,
                type: 'column',
                data: s.data,
            }));
            
            console.log('[OEE][workplaceChartSeries] Generated series:', series.length, series);
            if (series.length > 0) {
                console.log('[OEE][workplaceChartSeries] First series sample:', {
                    name: series[0].name,
                    type: series[0].type,
                    dataLength: series[0].data.length,
                    data: series[0].data,
                    nonNullCount: series[0].data.filter((v: any) => v != null).length,
                });
                console.log('[OEE][workplaceChartSeries] Second series sample:', {
                    name: series[1]?.name,
                    type: series[1]?.type,
                    dataLength: series[1]?.data.length,
                    data: series[1]?.data,
                    nonNullCount: series[1]?.data.filter((v: any) => v != null).length,
                });
                
                // Find a series with non-null data
                const seriesWithData = series.find((s: any) => s.data.some((v: any) => v != null));
                if (seriesWithData) {
                    console.log('[OEE][workplaceChartSeries] Series WITH data:', {
                        name: seriesWithData.name,
                        data: seriesWithData.data,
                        nonNullCount: seriesWithData.data.filter((v: any) => v != null).length,
                    });
                } else {
                    console.warn('[OEE][workplaceChartSeries] ⚠️ NO series has any non-null data!');
                }
            }
            
            return series as any;
        } else {
            // Average mode: Workplaces on X axis, bars for current/previous
            const base = [
                {
                    name: (this.$t('tpmAnalytics.oeeShort') as any) + ' – ' + (this.$t('tpmAnalytics.currentShort') as any),
                    type: 'column',
                    data: this.workplacesVisible.map((w) => toPct(w.oee)),
                },
            ] as any;
            // previous period series aligned by workplaceId
            const prevMap = new Map<string | null, OeeWorkplaceSummary>();
            for (const w of this.prevWorkplaces) prevMap.set(w.workplaceId ?? null, w);
            base.push({
                name: (this.$t('tpmAnalytics.oeeShort') as any) + ' – ' + (this.$t('tpmAnalytics.prevPeriodShort') as any),
                type: 'column',
                data: this.workplacesVisible.map((w) => toPct(prevMap.get(w.workplaceId ?? null)?.oee ?? null)),
            });
            // Per-workplace targets
            if (this.targetsMap && this.targetsMap.size > 0) {
                const world = this.workplacesVisible.map((w: any) => {
                    const t = this.targetsMap.get(w.workplaceId ?? null) as any;
                    return toPctTarget(t?.worldClass ?? null);
                });
                const goal = this.workplacesVisible.map((w: any) => {
                    const t = this.targetsMap.get(w.workplaceId ?? null) as any;
                    return toPctTarget(t?.oeeTarget ?? null);
                });
                base.push({ name: 'World Class', type: 'line', data: world });
                base.push({ name: 'Cíl OEE', type: 'line', data: goal });
            }
            return base;
        }
    }

    get workplacesSorted() {
        return [...this.workplaces].sort((a, b) => (b.oee ?? -1) - (a.oee ?? -1));
    }

    get workplacesVisible() {
        const all = this.workplacesSorted;
        if (this.wpStartIndex == null || this.wpEndIndex == null) return all;
        const start = Math.max(0, Math.min(all.length - 1, this.wpStartIndex));
        const end = Math.max(start, Math.min(all.length - 1, this.wpEndIndex));
        return all.slice(start, end + 1);
    }

    get workplaceTrendDataVisible() {
        const all = this.workplaceTrendData;
        if (this.wpStartIndex == null || this.wpEndIndex == null) return all;
        const start = Math.max(0, Math.min(all.length - 1, this.wpStartIndex));
        const end = Math.max(start, Math.min(all.length - 1, this.wpEndIndex));
        return all.slice(start, end + 1);
    }

    get detailHeaders() {
        return [
            { text: this.$t('tpmAnalytics.workplace') as any, value: 'workplaceName', align: 'left' },
            { text: this.$t('tpmAnalytics.oeeShort') as any, value: 'oee', align: 'right', sortable: true },
            { text: this.$t('tpmAnalytics.availability') as any, value: 'availability', align: 'right', sortable: true },
            { text: this.$t('tpmAnalytics.performance') as any, value: 'performance', align: 'right', sortable: true },
            { text: this.$t('tpmAnalytics.quality') as any, value: 'quality', align: 'right', sortable: true },
            { text: this.$t('tpmAnalytics.downtimeHours') as any, value: 'downtimeMinutes', align: 'right', sortable: true },
            { text: this.$t('tpmAnalytics.products') as any, value: 'products', align: 'right', sortable: true },
            { text: this.$t('tpmAnalytics.scraps') as any, value: 'scraps', align: 'right', sortable: true },
        ];
    }

    wpResetViewport() {
        const n = this.workplacesSorted.length;
        if (n === 0) {
            this.wpStartIndex = null;
            this.wpEndIndex = null;
            return;
        }

        // Zkusíme načíst uložený zoom z localStorage
        const savedZoom = loadCategoryZoom('oee-workplace', this.oeeGranularity);

        if (savedZoom && savedZoom.startIndex != null && savedZoom.endIndex != null) {
            // Použijeme uložený zoom, ale ověříme, že je v platném rozsahu
            if (savedZoom.startIndex >= 0 && savedZoom.endIndex < n) {
                this.wpStartIndex = savedZoom.startIndex;
                this.wpEndIndex = savedZoom.endIndex;
                return;
            }
        }

        // Pokud není uložený zoom nebo je neplatný, použijeme výchozí
        if (n <= this.wpWindow) {
            // No default zoom for up to wpWindow workplaces
            this.wpStartIndex = null;
            this.wpEndIndex = null;
            return;
        }
        // Apply default zoom (> wpWindow) from the very left
        const visible = Math.min(this.wpWindow, n);
        this.wpStartIndex = 0;
        this.wpEndIndex = visible - 1;

        // Uložíme výchozí zoom
        this.saveCurrentWorkplaceZoom();
    }

    wpShiftPercent(percent: number) {
        // Pro trend mode použijeme počet období, pro average mode počet pracovišť
        const n = this.workplaceChartMode === 'trend' 
            ? this.workplaceTrendData.length 
            : this.workplacesSorted.length;
        if (n === 0) return;
        if (this.wpStartIndex == null || this.wpEndIndex == null) {
            // initialize viewport to the leftmost slice
            const visible = Math.min(this.wpWindow, n);
            this.wpStartIndex = 0;
            this.wpEndIndex = visible - 1;
        }
        const span = this.wpEndIndex - this.wpStartIndex + 1;
        const delta = Math.max(1, Math.round(span * percent));
        let newStart = this.wpStartIndex + delta;
        let newEnd = this.wpEndIndex + delta;
        if (newStart < 0) {
            newStart = 0;
            newEnd = span - 1;
        }
        if (newEnd > n - 1) {
            newEnd = n - 1;
            newStart = Math.max(0, n - span);
        }
        this.wpStartIndex = newStart;
        this.wpEndIndex = newEnd;
        this.saveCurrentWorkplaceZoom();
    }

    wpZoomIn() {
        // Pro trend mode použijeme počet období, pro average mode počet pracovišť
        const n = this.workplaceChartMode === 'trend' 
            ? this.workplaceTrendData.length 
            : this.workplacesSorted.length;
        if (n === 0) return;
        if (this.wpStartIndex == null || this.wpEndIndex == null) {
            // initialize viewport to a slice to enable zooming
            const visible = Math.min(this.wpWindow, n);
            this.wpStartIndex = 0;
            this.wpEndIndex = visible - 1;
        }
        const span = this.wpEndIndex - this.wpStartIndex + 1;
        const minSpan = 2; // minimálně 2 položky
        const newSpan = Math.max(minSpan, Math.round(span * 0.75));
        const center = Math.round((this.wpStartIndex + this.wpEndIndex) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2) - 1;
        if (newStart < 0) {
            newStart = 0;
            newEnd = newSpan - 1;
        }
        if (newEnd > n - 1) {
            newEnd = n - 1;
            newStart = Math.max(0, n - newSpan);
        }
        this.wpStartIndex = newStart;
        this.wpEndIndex = newEnd;
        this.saveCurrentWorkplaceZoom();
    }

    wpZoomOut() {
        // Pro trend mode použijeme počet období, pro average mode počet pracovišť
        const n = this.workplaceChartMode === 'trend' 
            ? this.workplaceTrendData.length 
            : this.workplacesSorted.length;
        if (n === 0) return;
        if (this.wpStartIndex == null || this.wpEndIndex == null) {
            // nothing to zoom out from when showing all; keep showing all
            return;
        }
        const span = this.wpEndIndex - this.wpStartIndex + 1;
        const newSpan = Math.min(n, Math.round(span / 0.75));
        const center = Math.round((this.wpStartIndex + this.wpEndIndex) / 2);
        let newStart = center - Math.round(newSpan / 2);
        let newEnd = center + Math.round(newSpan / 2) - 1;
        if (newStart < 0) {
            newStart = 0;
            newEnd = newSpan - 1;
        }
        if (newEnd > n - 1) {
            newEnd = n - 1;
            newStart = Math.max(0, n - newSpan);
        }
        this.wpStartIndex = newStart;
        this.wpEndIndex = newEnd;
        this.saveCurrentWorkplaceZoom();
    }

    saveCurrentWorkplaceZoom() {
        if (this.wpStartIndex != null && this.wpEndIndex != null) {
            saveCategoryZoom('oee-workplace', {
                startIndex: this.wpStartIndex,
                endIndex: this.wpEndIndex,
                granularity: this.oeeGranularity,
            });
        }
    }
}
